import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert
} from '@mui/material';
import {
  School as SchoolIcon,
  FamilyRestroom as FamilyIcon,
  Event as EventIcon,
  PhotoLibrary as PhotoIcon,
  Description as DocumentIcon,
  HealthAndSafety as HealthIcon,
  AccountTree as TreeIcon,
  Forum as DiscussionIcon,
  EmojiEvents as GoalsIcon,
  Restaurant as RecipeIcon,
  ContactPhone as ContactIcon,
  AdminPanelSettings as AdminIcon,
  CheckCircleOutline as CheckIcon,
  ExpandMore as ExpandIcon,
  PlayCircleOutline as StartIcon,
  Lock as VaultIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// ── QUICK START STEPS ────────────────────────────────────────────────────────

const quickStartSteps = [
  {
    label: 'Create your account',
    description:
      'Sign up with your email address and choose a password. You\'ll then run through the Account Creation Wizard which sets up your family group and generates a unique Family Code.',
    action: null
  },
  {
    label: 'Set up your family',
    description:
      'During the wizard, choose whether you\'re creating a new family or joining an existing one. If creating: enter your family name and your role. If joining: enter the Family Code shared by your family administrator.',
    action: null
  },
  {
    label: 'Invite family members',
    description:
      'Once your family is set up, go to Admin Portal → User Management and send email invitations to family members. They\'ll receive a link to join directly.',
    action: '/admin'
  },
  {
    label: 'Explore the Family Board',
    description:
      'The Family Board is your home base. It shows recent announcements, upcoming events, photos, health updates, and quick links to all features — all in one view.',
    action: '/family-board'
  },
  {
    label: 'Complete your profile',
    description:
      'Add a photo, contact information, and birthday to your profile so family members can see and connect with you easily.',
    action: '/profile'
  }
];

// ── FEATURE GUIDES ────────────────────────────────────────────────────────────

const featureGuides = [
  {
    icon: <FamilyIcon />,
    color: '#1565c0',
    title: 'Family Board',
    path: '/family-board',
    summary: 'Your family\'s central dashboard',
    steps: [
      'Navigate to Family Board from the top navigation.',
      'View the widget grid showing Events, Announcements, Photos, Health Updates, and more.',
      'Click any widget\'s arrow icon to go directly to that feature.',
      'Use the Quick Actions panel for one-click access to common tasks.',
    ]
  },
  {
    icon: <EventIcon />,
    color: '#2e7d32',
    title: 'Events & Announcements',
    path: '/events',
    summary: 'Plan and share family events',
    steps: [
      'Go to Events to see upcoming family gatherings.',
      'Click "+ Add Event" to create a new event with title, date, time, location, and description.',
      'Family members can RSVP (Yes / No / Maybe) directly from the event.',
      'For Announcements, navigate to Announcements and click "+ New Announcement".',
      'Announcements appear on the Family Board for all family members.',
    ]
  },
  {
    icon: <DiscussionIcon />,
    color: '#6a1b9a',
    title: 'Messages & Discussions',
    path: '/messages',
    summary: 'Private family communication',
    steps: [
      'Click Messages in the top navigation to open your inbox.',
      'Start a direct message to any family member by clicking "New Conversation".',
      'For group discussions, go to Discussions and start a new thread.',
      'Reply to threads to keep conversations organized by topic.',
    ]
  },
  {
    icon: <PhotoIcon />,
    color: '#e65100',
    title: 'Photos',
    path: '/photos',
    summary: 'Share and preserve family memories',
    steps: [
      'Photos has 4 areas (5 for family admins): Personal Photos, Family Photos, Photo Vault, and Shared With Me.',
      'Personal Photos — private to you only. Upload, organize into folders, and copy or move files to the Vault or Family Photos.',
      'Family Photos — shared with all family members. Only admins can upload; all members can view and download.',
      'Photo Vault — your secure personal vault. Share individual photos with specific family members, or copy files to Family Photos.',
      'Family Photo Vault (admins only) — a locked vault with granular per-member access control for sensitive family images.',
      'Shared With Me — photos that other family members have explicitly shared with you from their personal vaults.',
      'Use the ⋮ menu on any file to Copy to Vault, Move to Vault, Copy to Family, Download, or Delete.',
    ]
  },
  {
    icon: <DocumentIcon />,
    color: '#4a148c',
    title: 'Documents',
    path: '/documents',
    summary: 'Store and share important files',
    steps: [
      'Documents has 4 areas (5 for admins): Personal Documents, Family Documents, Document Vault, and Shared With Me.',
      'Personal Documents — private to you. Upload PDFs, Word docs, spreadsheets, and more (up to 100MB). Create folders to organise files.',
      'Family Documents — shared read-only for all family members. Only admins can upload, rename, move, or delete. All members can download or copy to their own Personal Documents.',
      'Document Vault — your secure personal vault. Use it for sensitive files you may want to share selectively. Copy or move files from Personal Documents using the ⋮ menu.',
      'Family Document Vault (admins only) — a family-wide secure vault. Admins can copy or move files from Family Documents into here, then control which members can access each file.',
      'Shared With Me — documents that other family members have shared from their vaults.',
      'Tip: think of Personal Documents and Personal Photos as two separate drawers in your private filing cabinet — one for documents, one for photos.',
    ]
  },
  {
    icon: <HealthIcon />,
    color: '#b71c1c',
    title: 'Health Updates',
    path: '/health-updates',
    summary: 'Track family health information',
    steps: [
      'Navigate to Health Updates to view recent health entries.',
      'Click "Add Health Update" to log an update for yourself or a family member.',
      'Include a title, description, and associated member name.',
      'Health updates are visible to all family members — use this for general health news.',
      'For sensitive information, use the Documents section with restricted access.',
    ]
  },
  {
    icon: <TreeIcon />,
    color: '#004d40',
    title: 'Family Tree',
    path: '/family-tree',
    summary: 'Visualize your family history',
    steps: [
      'Open Family Tree to see an interactive visualization of your family structure.',
      'Click on any node to see that person\'s details.',
      'Use the controls to zoom in, zoom out, and pan around the tree.',
      'Family members are automatically populated from your family directory.',
    ]
  },
  {
    icon: <ContactIcon />,
    color: '#01579b',
    title: 'Family Directory',
    path: '/family-directory',
    summary: 'A private family address book',
    steps: [
      'Go to Family Directory to browse all family members.',
      'Click on a member to see their contact information, birthday, and profile.',
      'Update your own contact details from your Profile page.',
    ]
  },
  {
    icon: <VaultIcon />,
    color: '#37474f',
    title: 'Document Vault',
    path: '/documents',
    summary: 'Secure storage for sensitive documents',
    steps: [
      'The Vault tab is inside both the Documents and Photos sections.',
      'Your personal Vault is private by default — only you can see your vault files.',
      'Share individual vault files with specific family members using the Share (person+) icon.',
      'Copy or Move files between Personal Documents/Photos and your Vault using the ⋮ menu.',
      'Family Vault (admins only) — a separate family-wide vault. Admins upload directly or move files from Family Documents/Photos. Access is controlled per-member.',
      'Use the Vault for sensitive items: wills, insurance policies, medical records, legal documents.',
      'Admins can grant or revoke Family Vault access per-member from the Family Vault tab.',
    ]
  },
  {
    icon: <GoalsIcon />,
    color: '#f57f17',
    title: 'Goals',
    path: '/goals',
    summary: 'Track family goals together',
    steps: [
      'Navigate to Goals to see current family goals.',
      'Click "Add Goal" to create a new goal with a title, description, target date, and progress metric.',
      'Update progress as your family makes strides toward each goal.',
      'Goals visible to the whole family keep everyone motivated.',
    ]
  },
  {
    icon: <RecipeIcon />,
    color: '#bf360c',
    title: 'Recipes',
    path: '/recipes',
    summary: 'Your family recipe collection',
    steps: [
      'Open Recipes to browse your family\'s saved recipes.',
      'Click "Add Recipe" to contribute a new recipe with ingredients, instructions, and a photo.',
      'Filter recipes by category or search by name.',
      'Family members can add their own favorite recipes to build a shared cookbook.',
    ]
  },
  {
    icon: <AdminIcon />,
    color: '#880e4f',
    title: 'Admin Portal',
    path: '/admin',
    summary: 'Manage your family group (Admins only)',
    steps: [
      'Admins access the Admin Portal from the top navigation.',
      'User Management: invite new members, update roles, reset passwords, and manage 2FA.',
      'Groups: create sub-groups (e.g., "Parents", "Kids", "Extended") and assign members.',
      'Permissions: control which features each group can access.',
      'Audit Logs: see a record of all administrative actions for accountability.',
    ]
  },
];

// ── FAQ ───────────────────────────────────────────────────────────────────────

const faqs = [
  {
    q: 'How do I invite someone to my family?',
    a: 'Go to Admin Portal → User Management → Invite Member. Enter their email address and they\'ll receive an invitation to join. You must be an Admin or Owner to send invitations.'
  },
  {
    q: 'What is the Family Code?',
    a: 'Your Family Code is a unique identifier for your family group. It was generated when your family was created. New members can use it in the Account Creation Wizard to join your family.'
  },
  {
    q: 'Can I belong to more than one family?',
    a: 'Yes! FamilyForLife supports multi-family memberships. If you are part of multiple families (e.g., your own family and your parents\' family), you can switch between them using the Family Switcher in the navigation bar.'
  },
  {
    q: 'Who can see my personal documents?',
    a: 'Personal documents are private to you by default. You can explicitly share them with specific family members or the whole family when uploading or by editing the document\'s permissions later.'
  },
  {
    q: 'What is the difference between Member, Admin, and Owner?',
    a: 'Member: standard access to family features. Admin: can manage users, invite members, manage groups and permissions. Owner: full control including billing and account deletion.'
  },
  {
    q: 'How do I reset 2FA for a family member?',
    a: 'Admins can reset two-factor authentication for any member from Admin Portal → User Management. Click the "Reset 2FA" button next to the user and confirm.'
  },
  {
    q: 'Is my data private?',
    a: 'Absolutely. Your family data is only accessible to authenticated members of your family. FamilyForLife does not share or sell your data. See the Privacy Policy for full details.'
  },
  {
    q: 'What happens at the end of the free trial?',
    a: 'When your 14-day trial ends, access to premium features (photos, documents, health updates, goals, recipes, family directory) is restricted until you subscribe. Core features (family board, announcements, events, messages, discussions) remain available.'
  },
];

// ── COMPONENT ─────────────────────────────────────────────────────────────────

export default function TutorialsPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedGuide, setSelectedGuide] = useState(null);

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
          <SchoolIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Getting Started
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Everything you need to know to get the most out of FamilyForLife
          </Typography>
        </Box>
      </Box>

      {/* Quick Start */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Quick Start Guide
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Follow these steps to get your family up and running.
        </Typography>
        <Stepper activeStep={activeStep} orientation="vertical">
          {quickStartSteps.map((step, index) => (
            <Step key={step.label} expanded>
              <StepLabel
                onClick={() => setActiveStep(index)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {step.description}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  {step.action && (
                    <Button
                      component={Link}
                      to={step.action}
                      variant="outlined"
                      size="small"
                      startIcon={<StartIcon />}
                    >
                      Go there now
                    </Button>
                  )}
                  {index < quickStartSteps.length - 1 && (
                    <Button
                      onClick={() => setActiveStep(index + 1)}
                      size="small"
                      sx={{ ml: step.action ? 1 : 0 }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Feature Guides */}
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
        Feature Guides
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Click any feature to see step-by-step instructions.
      </Typography>

      {selectedGuide !== null ? (
        // Expanded guide view
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Avatar sx={{ bgcolor: featureGuides[selectedGuide].color }}>
              {featureGuides[selectedGuide].icon}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {featureGuides[selectedGuide].title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {featureGuides[selectedGuide].summary}
              </Typography>
            </Box>
            <Button
              component={Link}
              to={featureGuides[selectedGuide].path}
              variant="contained"
              size="small"
              startIcon={<StartIcon />}
            >
              Open feature
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <List>
            {featureGuides[selectedGuide].steps.map((step, i) => (
              <ListItem key={i} sx={{ py: 0.75, alignItems: 'flex-start' }}>
                <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: 'primary.main',
                      fontSize: 12,
                      fontWeight: 700
                    }}
                  >
                    {i + 1}
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary={step} />
              </ListItem>
            ))}
          </List>
          <Box sx={{ mt: 2 }}>
            <Button onClick={() => setSelectedGuide(null)} size="small">
              Back to all guides
            </Button>
          </Box>
        </Paper>
      ) : (
        // Guide cards grid
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {featureGuides.map((guide, index) => (
            <Grid item xs={12} sm={6} md={4} key={guide.title}>
              <Card
                elevation={1}
                sx={{ height: '100%', borderRadius: 2, '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}
              >
                <CardActionArea
                  onClick={() => setSelectedGuide(index)}
                  sx={{ height: '100%', p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                >
                  <Avatar sx={{ bgcolor: guide.color, flexShrink: 0 }}>
                    {guide.icon}
                  </Avatar>
                  <CardContent sx={{ p: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {guide.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {guide.summary}
                    </Typography>
                    <Chip
                      label={`${guide.steps.length} steps`}
                      size="small"
                      sx={{ mt: 1, fontSize: '0.7rem', height: 20 }}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tips */}
      <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Pro Tips
        </Typography>
        <List dense disablePadding>
          {[
            'Use the Family Board as your daily starting point — everything important surfaces there.',
            'Set up push notifications in your browser to get alerts for new events and messages.',
            'Admins: create Groups (e.g., "Parents", "Kids") to control feature access per group.',
            'The Family Switcher in the nav bar lets you toggle between families if you belong to more than one.',
          ].map((tip) => (
            <ListItem key={tip} sx={{ py: 0.25, pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckIcon color="info" fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={tip} />
            </ListItem>
          ))}
        </List>
      </Alert>

      {/* FAQ */}
      <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
        Frequently Asked Questions
      </Typography>
      {faqs.map((faq) => (
        <Accordion key={faq.q} elevation={1} sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="subtitle2" fontWeight={600}>
              {faq.q}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">
              {faq.a}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Still need help */}
      <Paper
        elevation={0}
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          bgcolor: 'action.hover',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" gutterBottom>
          Still need help?
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Our support team is here for you. Open a ticket and we'll respond promptly.
        </Typography>
        <Button
          component={Link}
          to="/help-center"
          variant="contained"
        >
          Contact Support
        </Button>
      </Paper>
    </Box>
  );
}
